import { useEffect, useRef, useState } from "react";
import { SignalTracker, chunkBounds, levelWorkletUrl } from "@/lib/signal";

const MIME = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];

// Records mic in back-to-back chunks: the next recorder starts before the previous one stops, so there is no gap.
// Where to cut is decided on the audio clock (lib/signal.js): at a short pause after the chunk's minimum length, or at
// its maximum, so words aren't sliced in half and background tabs (which slow timers) don't stretch chunks. A chunk
// with no speech is not uploaded, only reported (onSilent). If the audio tap can't run, it falls back to a timer.
// Online meetings pass `meeting` (the call's audio): it is recorded as a second track ("them") with the same chunk
// index and offset as the mic ("me"). The meeting stream belongs to the caller and survives pauses.
// micId picks a microphone; changing it ends the current chunk early and carries on with the new mic.
export function useRecorder({ active, chunkSeconds, baseIndex, anchorMs, onChunk, onSilent, onError, onMicMissing, meeting = null, micTrack = "room", micId = "" }) {
  const [capturing, setCapturing] = useState(false);
  const [liveStream, setLiveStream] = useState(null);
  const [deadMic, setDeadMic] = useState(false);
  const [cutting, setCutting] = useState("timer");
  const cb = useRef({ onChunk, onSilent, onError, onMicMissing });
  cb.current = { onChunk, onSilent, onError, onMicMissing };
  const indexRef = useRef(null);

  useEffect(() => {
    if (!active || anchorMs == null) return undefined;
    if (indexRef.current == null) indexRef.current = baseIndex || 0;
    let stream;
    let current;
    let timer;
    let watchdog;
    let ctx;
    let cancelled = false;
    let lastFrame = 0;
    let chunkStartedAt = 0;
    const bounds = chunkBounds(chunkSeconds);
    const tracker = new SignalTracker(bounds);
    const mimeType = MIME.find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m));

    const record = (source, idx, offset) => {
      const rec = new MediaRecorder(source, mimeType ? { mimeType, audioBitsPerSecond: 32000 } : undefined);
      const parts = [];
      rec.ondataavailable = (e) => e.data && e.data.size && parts.push(e.data);
      rec.onstop = () => {
        if (rec._silent) {
          cb.current.onSilent?.(idx, Math.max(0, offset), rec._seconds, rec._track);
          return;
        }
        const blob = new Blob(parts, { type: rec.mimeType || "audio/webm" });
        if (blob.size > 2000) cb.current.onChunk(blob, idx, Math.max(0, offset), rec._track);
      };
      rec.start();
      return rec;
    };
    const meetingLive = () => !!meeting && meeting.getAudioTracks().some((t) => t.readyState === "live");
    const startOne = () => {
      const idx = indexRef.current++;
      const offset = (Date.now() - anchorMs) / 1000;
      const mic = record(stream, idx, offset);
      mic._track = micTrack; // "me" for every online chunk, even while call audio is disconnected
      const recs = [mic];
      if (meetingLive()) {
        const them = record(meeting, idx, offset);
        them._track = "them";
        recs.push(them);
      }
      return recs;
    };
    const stopAll = (recs) => recs?.forEach((r) => r.state !== "inactive" && r.stop());
    const rotate = (stats) => {
      const prev = current;
      if (stats) {
        for (const r of prev || []) {
          r._silent = r._track === "them" ? stats.silent.call : stats.silent.mic;
          r._seconds = Math.round(stats.seconds * 10) / 10;
        }
      }
      current = startOne();
      chunkStartedAt = Date.now();
      stopAll(prev);
    };

    // The audio tap: input 0 is the mic, input 1 the call audio. Its frames drive the cuts.
    const startTap = async () => {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx || !window.AudioWorkletNode) return false;
      ctx = new Ctx();
      await ctx.audioWorklet.addModule(levelWorkletUrl());
      if (cancelled) return false;
      const node = new AudioWorkletNode(ctx, "level-tap", { numberOfInputs: 2, numberOfOutputs: 1 });
      ctx.createMediaStreamSource(stream).connect(node, 0, 0);
      if (meetingLive()) ctx.createMediaStreamSource(meeting).connect(node, 0, 1);
      const mute = ctx.createGain();
      mute.gain.value = 0;
      node.connect(mute).connect(ctx.destination); // keeps the node pulled; nothing is audible
      node.port.onmessage = ({ data }) => {
        lastFrame = Date.now();
        if (tracker.frame(data)) rotate(tracker.take());
        setDeadMic(tracker.dead);
      };
      if (ctx.state === "suspended") await ctx.resume().catch(() => {});
      return true;
    };
    (async () => {
      const audio = { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true, autoGainControl: true };
      try {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ audio: micId ? { ...audio, deviceId: { exact: micId } } : audio });
        } catch (e) {
          // The remembered mic was unplugged or belongs to another computer: carry on with the default one.
          if (!micId || !["OverconstrainedError", "NotFoundError", "NotReadableError"].includes(e?.name)) throw e;
          cb.current.onMicMissing?.();
          stream = await navigator.mediaDevices.getUserMedia({ audio });
        }
      } catch (e) {
        cb.current.onError?.(e);
        return;
      }
      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      current = startOne();
      chunkStartedAt = Date.now();
      setCapturing(true);
      setLiveStream(stream);
      const tapped = await startTap().catch(() => false);
      if (cancelled) return;
      setCutting(tapped ? "pause-aware" : "timer");
      if (!tapped) {
        timer = setInterval(() => rotate(null), chunkSeconds * 1000);
        return;
      }
      // Safety net if the tap stops delivering (audio context suspended): cut on the wall clock, never past the maximum.
      watchdog = setInterval(() => {
        const stalled = Date.now() - lastFrame > 3000;
        if (stalled && Date.now() - chunkStartedAt > (bounds.maxS + 2) * 1000) {
          tracker.take();
          rotate(null);
        }
        setCutting(stalled ? "timer" : "pause-aware");
      }, 1000);
    })();

    return () => {
      cancelled = true;
      clearInterval(timer);
      clearInterval(watchdog);
      ctx?.close().catch(() => {});
      setDeadMic(false);
      stopAll(current);
      setTimeout(() => stream && stream.getTracks().forEach((t) => t.stop()), 300);
      setCapturing(false);
      setLiveStream(null);
    };
  }, [active, chunkSeconds, anchorMs, baseIndex, meeting, micTrack, micId]);

  return { capturing, stream: liveStream, deadMic, cutting };
}
